import React, { useState } from 'react';
import { Button, Content, DateRangePicker } from 'rsuite';
import { format, toDate, nextSaturday } from 'date-fns';
import { LoadingMenu, LoadingSpinerArea } from '../../../../../../components/LoadingSpinner';
import SelectPill from '../../../../../../components/SelectPill';
import DateRangePickerPill from '../../../../../../components/DateRangePickerPill';
import { FilterPreference } from '../FilterPreference';
import { ModalSavingFilters } from '../ModalSavingFilters';
import calendarIcon from '../../../../../../assets/icons/icon_calendar.svg';
import SaveIcon from '@material-ui/icons/Save';

import { useAderenciaFilters } from '../../hooks/useAderenciaPrecosFilters';
import { useAderenciaPrecosSavedFilter } from '../../hooks/useAderenciaPrecosSavedFilter';
import { useAderenciaPrecosSavingFilters } from '../../hooks/useAderenciaPrecosSavingFilters';
import { useAderenciaProductFilter } from '../../hooks/useAderenciaProductFilter';
import { useAderenciaPrecosStore } from '../../AderenciaPrecos.store';
import { FilterNames, FiltersData } from '../../types';
import { dateLocale } from '../../../InfoPanel/utils';
import { FILTER_LOCALE } from '../../data';
import styles from './FiltersArea.module.scss';
import { usePromiseTracker } from 'react-promise-tracker';

const { after } = DateRangePicker;

const aderenciaHistoryArea = 'aderencia-history';
const aderenciaBigNumbersArea = 'aderencia-big-numbers';
const aderenciaDetailsDownloadArea = 'aderencia-details-download';
const aderenciaRangesArea = 'aderencia-ranges';

type FiltersAreaProps = {
  onClose?: (name: FilterNames) => void;
  onClean?: (name: FilterNames) => void;
  onSelectSavedFilter: (id: number) => void;
  onCleanSavedFilter: () => void;
  onDateClean?: () => void;
  onDateChange?: (startDate: string, endDate: string) => void;
  onChangePreference?: () => void;
};

const renderMenu = (menu: React.ReactNode) => (
  <span>
    <LoadingMenu area="aderencia-filter" />
    {menu}
  </span>
);

export const FiltersArea = ({ onClose, onClean, onSelectSavedFilter, onCleanSavedFilter, onDateChange, onDateClean, onChangePreference }: FiltersAreaProps) => {
  const [dateRangeValue, setDateRangeValue] = useState([]);
  const [savedFilter, setSavedFilter] = useState<number | null>(null);
  const {
    produto,
    rede,
    tipoLoja,
    uf,
    segmento,
    savedFilterSelectList,
    savingFiltersData,
    showModalSavingFilters,
    aderenciaPreferenceMutateData,
    aderenciaBodyRequest,
    setAderenciaPrecosState,
    setAderenciaSavingFiltersName,
    setAderenciaCleanFilters,
  } = useAderenciaPrecosStore();

  const { handleAderenciaFitersSearch, handleCleanAderenciaFilter, handleSelectAderenciaFilter } = useAderenciaFilters();

  const { handleGetSavedFilterList, handleGetSavedFilterSearch } = useAderenciaPrecosSavedFilter();
  const { handleAderenciaProductFilterSearch, handleCleanAderenciaProductFilter, handleSelectAderenciaProductFilter } = useAderenciaProductFilter();
  const { handleAderenciaSaveFilters } = useAderenciaPrecosSavingFilters();

  const hasFilterSelected = [produto, rede, tipoLoja, uf, segmento].some(filter => (filter?.value?.length ?? 0) > 0);

  const handleOnClean = (name: FilterNames) => {
    if (name === 'PRODUTO') {
      handleCleanAderenciaProductFilter();
    } else {
      handleCleanAderenciaFilter(name);
    }
    onClean?.(name);
  };

  const handleSaveFilters = () => {
    const today = new Date();
    const body = {
      ...savingFiltersData,
      filtros: {
        dataInicial: format(today, 'yyyy-MM-dd'),
        produto: aderenciaBodyRequest.produto,
        rede: aderenciaBodyRequest.rede,
        tipoLoja: aderenciaBodyRequest.tipoLoja,
        uf: aderenciaBodyRequest.uf,
        segmento: aderenciaBodyRequest.segmentosPersonalizados,
      },
    };

    if (savingFiltersData?.nomeFiltroSalvo !== '') {
      handleAderenciaSaveFilters(body);
      setAderenciaPrecosState('showModalSavingFilters', false);
    }
  };

  const handleCloseSavingFiltersModal = () => {
    setAderenciaPrecosState('showModalSavingFilters', false);
    setAderenciaSavingFiltersName('');
  };

  const handleDateChange = (value: any) => {
    const startDate = value[0] ? format(toDate(value[0]), 'yyyy-MM-dd') : '';
    const endDate = value[1] ? format(toDate(value[1]), 'yyyy-MM-dd') : '';
    setDateRangeValue(value);
    onDateChange?.(startDate, endDate);
  };

  const handleDateClean = () => {
    setDateRangeValue([]);
    onDateClean?.();
  };

  const handleCleanFilters = () => {
    setDateRangeValue([]);
    setAderenciaCleanFilters();
    onCleanSavedFilter?.();
  };

  const handleSelectSavedFilter = (id: number) => {
    setDateRangeValue([]);
    setAderenciaCleanFilters();
    setSavedFilter(id);
    onSelectSavedFilter?.(id);
  };

  const handleCleanAllFilters = () => {
    handleCleanFilters();
    setSavedFilter(null);
  };

  const rawFiltersConfig: { placeholder: string; name: string; value: FiltersData['value']; data: FiltersData['list']; param: FilterNames }[] = [
    { placeholder: 'Rede', name: 'rede-filter-aderencia', value: rede.value, data: rede.list, param: 'REDE' },
    { placeholder: 'Tipo de loja', name: 'tipo-loja-filter-aderencia', value: tipoLoja.value, data: tipoLoja.list, param: 'TIPO_LOJA' },
    { placeholder: 'UF’s', name: 'uf-filter-aderencia', value: uf.value, data: uf.list, param: 'UF' },
    { placeholder: 'Segmentos personalizados', name: 'custom-segments', value: segmento.value, data: segmento.list, param: 'SEGMENTO' },
  ];

  const filtersConfig = rawFiltersConfig.filter(f => f.param !== 'SEGMENTO' || (f.data?.length ?? 0) > 0);

  const filterDisabled = (param: FilterNames) => {
    if (param === 'SEGMENTO') {
      return (rede.value?.length ?? 0) > 0;
    }

    if (param === 'REDE') {
      return (segmento.value?.length ?? 0) > 0;
    }

    return false;
  };

  const { promiseInProgress: isLoadingHistory } = usePromiseTracker({ area: aderenciaHistoryArea });
  const { promiseInProgress: isLoadingBigNumbers } = usePromiseTracker({ area: aderenciaBigNumbersArea });
  const { promiseInProgress: isLoadingDetailsDownload } = usePromiseTracker({ area: aderenciaDetailsDownloadArea });
  const { promiseInProgress: isLoadingRanges } = usePromiseTracker({ area: aderenciaRangesArea });

  const isLoading = isLoadingHistory || isLoadingBigNumbers || isLoadingDetailsDownload || isLoadingRanges;

  return (
    <Content className={styles['filters-area-content']}>
      <LoadingSpinerArea isLoading={isLoading} size="md" style={{ position: 'absolute' }} />
      <div className={styles['inner-div']}>
        <div className={styles.row}>
          <div className={styles.column}>
            <DateRangePickerPill
              placeholder="Selecione semanas específicas"
              leftIcon={<img src={calendarIcon} alt="" style={{ height: '12px' }} />}
              value={dateRangeValue}
              ranges={[]}
              locale={dateLocale}
              hoverRange="week"
              format="DD/MM/YYYY"
              onOk={handleDateChange}
              onClean={handleDateClean}
              disabledDate={after(nextSaturday(new Date()))}
            />
            <SelectPill
              type="check"
              id="check-picker-pill"
              placeholder="Produto"
              name="produto"
              value={produto.value}
              data={produto.list}
              onOpen={() => handleAderenciaProductFilterSearch('')}
              onSearch={value => handleAderenciaProductFilterSearch(value)}
              onSelect={(_v, item) => handleSelectAderenciaProductFilter(item)}
              onClean={() => handleOnClean('PRODUTO')}
              onClose={() => onClose?.('PRODUTO')}
              locale={FILTER_LOCALE}
              renderMenu={renderMenu}
            />
          </div>
          <div className={styles.column}>
            <Button id="clean-aderencia-filters" className={styles['btn-clear-filters']} onClick={handleCleanAllFilters}>
              Limpar filtros
            </Button>
            <Button
              id="save-aderencia-filters"
              className={styles['btn-save-filters']}
              disabled={!hasFilterSelected}
              onClick={() => setAderenciaPrecosState('showModalSavingFilters', true)}
            >
              <SaveIcon className={styles['save-icon']} />
              Salvar filtros
            </Button>
          </div>
        </div>
        <div className={styles.row}>
          <div className={styles.column}>
            {filtersConfig.map(({ placeholder, name, value, data, param }) => (
              <SelectPill
                type="check"
                id="check-picker-pill"
                placeholder={placeholder}
                name={name}
                value={value}
                data={data}
                onOpen={() => handleAderenciaFitersSearch(param, '')}
                onSearch={(value: string) => handleAderenciaFitersSearch(param, value)}
                onSelect={(_v, item) => handleSelectAderenciaFilter(param, item)}
                onClean={() => handleOnClean(param)}
                onClose={() => onClose?.(param)}
                locale={FILTER_LOCALE}
                renderMenu={renderMenu}
                key={name}
                disabled={filterDisabled(param)}
              />
            ))}
          </div>

          <div className={styles.column}>
            <SelectPill
              type="select"
              id="select-picker-pill"
              placeholder="Filtros salvos"
              name="produto-concorrente"
              value={savedFilter || null}
              data={savedFilterSelectList}
              onOpen={handleGetSavedFilterList}
              onSearch={handleGetSavedFilterSearch}
              onSelect={(id: number) => handleSelectSavedFilter(id)}
              onClean={handleCleanAllFilters}
              locale={FILTER_LOCALE}
              renderMenu={renderMenu}
            />
            <FilterPreference data={aderenciaPreferenceMutateData} onChangePreference={onChangePreference} />
          </div>
        </div>
      </div>
      <ModalSavingFilters
        show={showModalSavingFilters}
        nome={savingFiltersData?.nomeFiltroSalvo}
        onClose={handleCloseSavingFiltersModal}
        onChange={name => setAderenciaSavingFiltersName(name)}
        onClickSalvar={handleSaveFilters}
      />
    </Content>
  );
};
